// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import AddAllToList from "@bg/App/Components/Cart/AddAllToList";
import CartSaveOrderButton from "@bg/Overrides/Widgets/Cart/CartSaveOrderButton";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/**
 * XNG-Change
 * BG-2729
 * Create OwnProps type and add mapDispatchToProps and mapStateToProps
 * add isOnePageCheckout to:
 *  - hide items count and condensed checkbox
 *  - show save cart, edit cart and add all to list links
 * add isOrderConfirmationPage to:
 *  - hide items count and condensed checkbox
 *  - show save cart and add all to list links
 */
type OwnProps = {
    productsCount: number;
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent, value: boolean) => void;
    extendedStyles?: CartLinesListHeaderStyles;
    // XNG-Changes
    isOnePageCheckout?: boolean;
    isOrderConfirmationPage?: boolean;
    isQuote?: boolean;
    cartId?: string;
};

const mapStateToProps = (state: ApplicationState) => ({
    savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    rfqQuoteDetailsUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface CartLinesListHeaderStyles {
    wrapper?: InjectableCss;
    productCountText?: TypographyPresentationProps;
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedCheckbox?: CheckboxPresentationProps;
    // XNG-Change
    saveOrderLink?: LinkPresentationProps;
    addAllToListLink?: LinkPresentationProps;
    cartLink?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
}

export const cartLinesListHeaderStyles: CartLinesListHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: 20px;
            align-items: baseline;
            padding-bottom: 16px;
        `,
    },
    productCountText: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    checkboxGroup: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
};

const CartLinesListHeader: FC<Props> = ({
    productsCount,
    onChangeIsCondensed,
    extendedStyles,
    isCondensed,
    hideCondensedSelector,
    // XNG-Change
    isOnePageCheckout,
    history,
    cartPageUrl,
    isOrderConfirmationPage,
    cartId,
    isQuote,
    rfqQuoteDetailsUrl,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartLinesListHeaderStyles, extendedStyles));

    const productsLabel = productsCount > 1 ? "Products" : "Product";

    // XNG-Changes
    const handleEditCartClick = () => {
        if (isQuote) {
            history.push(`${rfqQuoteDetailsUrl}?quoteId=${cartId}`);

            return;
        }
        history.push(`${cartPageUrl}`);
    };

    return (
        <StyledWrapper {...styles.wrapper} {...{ isOnePageCheckout, isOrderConfirmationPage }}>
            <Typography {...styles.productCountText}>
                {!isOnePageCheckout && !isOrderConfirmationPage && productsCount} {translate(productsLabel)}
            </Typography>
            {/* XNG-Changes */}
            {(isOnePageCheckout || isOrderConfirmationPage) && (
                // <StyledWrapper {...styles.actionsWrapper}>
                <>
                    {!isOrderConfirmationPage && (
                        <>
                            {isQuote ? (
                                <Link
                                    data-test-selector="onePageCheckout_EditCart"
                                    {...styles.cartLink}
                                    onClick={handleEditCartClick}
                                >
                                    {translate("View Quote Details")}
                                </Link>
                            ) : (
                                <Link
                                    data-test-selector="onePageCheckout_EditCart"
                                    {...styles.cartLink}
                                    onClick={handleEditCartClick}
                                >
                                    {translate("Edit Cart")}
                                </Link>
                            )}
                        </>
                    )}
                    {cartId === "current" && (
                        <CartSaveOrderButton
                            variant="link"
                            extendedStyles={styles.saveOrderLink}
                            showSaveOrderConfirmationModal={isOnePageCheckout}
                        />
                    )}
                    <AddAllToList variant="link" extendedStyles={styles.addAllToListLink} cartId={cartId} />
                </>
            )}
            {!hideCondensedSelector && !isOnePageCheckout && (
                <CheckboxGroup {...styles.checkboxGroup}>
                    <Checkbox {...styles.condensedCheckbox} checked={isCondensed} onChange={onChangeIsCondensed}>
                        {translate("Condensed View")}
                        <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                    </Checkbox>
                </CheckboxGroup>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(CartLinesListHeader));
